import type { MemberRole } from '../types/organisation';
import { hasAnyRole } from './roleUtils';
import {
  hasPlatformOrgAccess,
  isPlatformOrgAdminUserRole,
  isSuperAdminUserRole,
} from './platformAdminAccess';

/** Roles that inherit moderator feature access (organisationAdmin ⊃ moderator). */
export const MODERATOR_PRIVILEGE_ROLES = [
  'superAdmin',
  'organisationAdmin',
  'moderator',
] as const satisfies readonly MemberRole[];

/** Roles allowed on org settings (includes platform org admin). */
export const ORG_SETTINGS_ROLES = [
  'superAdmin',
  'admin',
  'organisationAdmin',
  'moderator',
] as const;

export const PROJECTS_NAV_ROLES = [
  'moderator',
  'organisationAdmin',
  'steward',
] as const;

export const DASHBOARD_NAV_ROLES = [
  'moderator',
  'organisationAdmin',
  'facilitator',
] as const;

export function hasUserRole(
  userRole: string | null | undefined,
  allowed: readonly string[]
): boolean {
  return Boolean(userRole && (allowed as readonly string[]).includes(userRole));
}

/**
 * Moderator-level privileges: organisationAdmin inherits moderator access.
 * Platform org admins (`admin`) are included where pages already grant them access.
 */
export function hasModeratorPrivileges(userRole?: string | null): boolean {
  return hasUserRole(userRole, MODERATOR_PRIVILEGE_ROLES) || hasPlatformOrgAccess(userRole);
}

/** Member `roles[]` check: organisationAdmin inherits moderator (matches login). */
export function memberHasModeratorPrivileges(roles?: MemberRole[]): boolean {
  return hasAnyRole(roles, ['moderator', 'organisationAdmin']);
}

/** Org-admin-level privileges (stricter than moderator — e.g. bulletin delete). */
export function hasOrgAdminPrivileges(userRole?: string | null): boolean {
  return isSuperAdminUserRole(userRole) || userRole === 'organisationAdmin';
}

export function canAccessModeratorDashboard(userRole?: string | null): boolean {
  return hasUserRole(userRole, MODERATOR_PRIVILEGE_ROLES);
}

export function canAccessOrgDashboard(userRole?: string | null): boolean {
  return hasOrgAdminPrivileges(userRole) || hasPlatformOrgAccess(userRole);
}

export function canAccessOrgSettings(userRole?: string | null): boolean {
  return hasUserRole(userRole, ORG_SETTINGS_ROLES);
}

export function canManageOrgSettingsDestructive(userRole?: string | null): boolean {
  return hasOrgAdminPrivileges(userRole);
}

export function canCreateProjects(userRole?: string | null): boolean {
  return hasUserRole(userRole, MODERATOR_PRIVILEGE_ROLES);
}

export function canAccessWorkshopDashboard(userRole?: string | null): boolean {
  return hasModeratorPrivileges(userRole);
}

/** Role may edit any bulletin (author check remains at call site). */
export function canEditBulletin(userRole?: string | null): boolean {
  return hasUserRole(userRole, MODERATOR_PRIVILEGE_ROLES);
}

/** Role may delete any bulletin (stricter than edit). */
export function canDeleteBulletin(userRole?: string | null): boolean {
  return hasOrgAdminPrivileges(userRole);
}

/** Bypass target-role filters on bulletin/org queries. */
export function canBypassBulletinTargetRoles(userRole?: string | null): boolean {
  return hasUserRole(userRole, MODERATOR_PRIVILEGE_ROLES);
}

export function canSeeProjectsNav(userRole?: string | null): boolean {
  return hasUserRole(userRole, PROJECTS_NAV_ROLES);
}

export function canSeeWorkshopsNav(userRole?: string | null): boolean {
  return canSeeProjectsNav(userRole);
}

export function canSeeOrgDashboardNavLink(userRole?: string | null): boolean {
  return hasUserRole(userRole, ['organisationAdmin', 'superAdmin']);
}

export function canSeeModeratorDashboardNavLink(userRole?: string | null): boolean {
  return hasUserRole(userRole, ['organisationAdmin', 'superAdmin']);
}

export function canSeeFacilitatorOrModeratorDashboardNav(
  userRole?: string | null
): boolean {
  return hasUserRole(userRole, DASHBOARD_NAV_ROLES);
}

export function canSeePinsEvaluationNav(userRole?: string | null): boolean {
  return Boolean(userRole) && userRole !== 'facilitator' && userRole !== 'superAdmin';
}

export function canUseStewardAdminButtonRow(userRole?: string | null): boolean {
  return userRole === 'steward';
}

/** Steward button-row items that remain superAdmin-only (hidden from stewards). */
export const STEWARD_SUPER_ADMIN_ONLY_VIEWS = [
  'user-access-view',
  'orphaned-files',
] as const;

export function isStewardSuperAdminOnlyView(view: string): boolean {
  return (STEWARD_SUPER_ADMIN_ONLY_VIEWS as readonly string[]).includes(view);
}

export function canSeeOrgSettingsNav(userRole?: string | null): boolean {
  return hasUserRole(userRole, ['organisationAdmin', 'moderator']);
}

/** Whether session role should load all projects like a platform admin. */
export function canLoadAllProjects(userRole?: string | null): boolean {
  return hasPlatformOrgAccess(userRole);
}

export function isOrganisationAdminUserRole(
  userRole?: string | null
): userRole is 'organisationAdmin' {
  return userRole === 'organisationAdmin';
}

export function isModeratorUserRole(userRole?: string | null): userRole is 'moderator' {
  return userRole === 'moderator';
}

export function isStewardUserRole(userRole?: string | null): userRole is 'steward' {
  return userRole === 'steward';
}

export function isFacilitatorUserRole(userRole?: string | null): userRole is 'facilitator' {
  return userRole === 'facilitator';
}

export function isProjectAdminUserRole(userRole?: string | null): userRole is 'projectAdmin' {
  return userRole === 'projectAdmin';
}

export function isWanderWriterUserRole(userRole?: string | null): userRole is 'wanderWriter' {
  return userRole === 'wanderWriter';
}

export {
  hasPlatformOrgAccess,
  isPlatformOrgAdminUserRole,
  isSuperAdminUserRole,
};
